import type { DriverReadiness, ParticipantKyc, ShipmentResponse, ShipmentStatus } from '../../api/types';

/**
 * Where a participant's KYC stops the shipment, as rhino-api enforces it (R18, rhino-api#294):
 * award blocks on the shipper, then the winning bidder; AWARDED → ASSIGNED blocks on the partner;
 * after assignment an unverified shipper or partner is only an ops alert. The driver is checked
 * at trip start through `driverReadiness`, never here.
 */
export type KycGateEffect = 'BLOCKS_AWARD' | 'BLOCKS_ASSIGN' | 'ALERT' | 'OK';

export interface KycGateRow {
  entry: ParticipantKyc;
  effect: KycGateEffect;
}

/** AWARDED…AT_DROP: the window in which an unverified shipper/partner is flagged to ops. */
const ALERT_STATUSES: readonly ShipmentStatus[] = [
  'AWARDED',
  'ASSIGNED',
  'EN_ROUTE_PICKUP',
  'AT_PICKUP',
  'LOADED',
  'IN_TRANSIT',
  'AT_DROP',
];

/** MISSING = nobody holds the slot (e.g. no award yet): nothing to verify, so nothing is gated. */
function notVerified(entry: ParticipantKyc): boolean {
  return entry.kycStatus !== 'VERIFIED' && entry.kycStatus !== 'MISSING';
}

function effectFor(status: ShipmentStatus, entry: ParticipantKyc): KycGateEffect {
  if (entry.role === 'DRIVER' || !notVerified(entry)) return 'OK';
  if (status === 'POSTED') return 'BLOCKS_AWARD';
  if (status === 'AWARDED' && entry.role === 'PARTNER') return 'BLOCKS_ASSIGN';
  return ALERT_STATUSES.includes(status) ? 'ALERT' : 'OK';
}

export function kycGate(
  shipment: Pick<ShipmentResponse, 'status' | 'participantKyc'> | null | undefined,
): KycGateRow[] {
  if (!shipment?.participantKyc) return [];
  return shipment.participantKyc.map((entry) => ({ entry, effect: effectFor(shipment.status, entry) }));
}

/** The gate row for one role, if the API sent it. */
export function gateFor(rows: KycGateRow[], role: ParticipantKyc['role']): KycGateRow | undefined {
  return rows.find((row) => row.entry.role === role);
}

/**
 * Who stops awarding this bid: the shipper first, then the bidder (`bidderKycVerified === false`).
 * Unknown (null / absent) is never treated as blocked.
 */
export function bidAwardBlocker(
  shipment: Pick<ShipmentResponse, 'status' | 'participantKyc'> | null | undefined,
  bid: { bidderKycVerified?: boolean | null },
): 'SHIPPER' | 'BIDDER' | null {
  if (gateFor(kycGate(shipment), 'SHIPPER')?.effect === 'BLOCKS_AWARD') return 'SHIPPER';
  return bid.bidderKycVerified === false ? 'BIDDER' : null;
}

export type ReadinessItem = 'KYC' | 'RC' | 'photo';

/** The driver-readiness items still not VERIFIED; empty when ready or unknown. */
export function readinessMissing(readiness: DriverReadiness | null | undefined): ReadinessItem[] {
  if (!readiness || readiness.ready) return [];
  const missing: ReadinessItem[] = [];
  if (readiness.kyc !== 'VERIFIED') missing.push('KYC');
  if (readiness.vehicleRc !== 'VERIFIED') missing.push('RC');
  if (readiness.vehiclePhoto !== 'VERIFIED') missing.push('photo');
  return missing;
}
